import type { ChatAdapter, ChatConversation, ChatMessage, ChatUser } from '@mui/x-chat/headless';
import { apiFetch } from '../../utils/api';

type HypeResponseModel = {
  answer: string,
  choices: string[]
}

export const initialMessages: ChatMessage[] = [
  {
    id: 'greeting-msg',
    conversationId: 'conversation-1',
    role: 'assistant',
    parts:[ 
      {
        type: 'text',
        text: "Hello! I'm HYPE AI. I can help you design statistically rigorous experiments, calculate sample sizes, recommend appropriate methodologies, and interpret results. How can I assist?",
      },
    ],
    createdAt: new Date().toISOString(),
    author: {
        id: 'assistant-1',
        displayName: 'Hype AI',
        // avatarUrl?: string;
        role: 'assistant',
      },
  },
   {
    id: 'greeting-choice-1',
    conversationId: 'conversation-1',
    role: 'assistant',
    parts:[ 
      {
        type: 'text',
        text: "Calculate minimum sample size for 80% power",
      },
    ],
    createdAt: new Date().toISOString(),
    author: {
        id: 'assistant-1',
        displayName: 'Hype AI',
        // avatarUrl?: string;
        role: 'assistant',
      },
  },
  {
    id: 'greeting-choice-2',
    conversationId: 'conversation-1',
    role: 'assistant',
    parts:[ 
      {
        type: 'text',
        text: "Recommend experiment type for incremental sales",
      },
    ],
    createdAt: new Date().toISOString(),
    author: {
        id: 'assistant-1',
        displayName: 'Hype AI',
        // avatarUrl?: string;
        role: 'assistant',
      },
  },
  {
    id: 'greeting-choice-3',
    conversationId: 'conversation-1',
    role: 'assistant',
    parts:[ 
      {
        type: 'text',
        text: "Explain geo-matched market methodology",
      },
    ],
    createdAt: new Date().toISOString(),
    author: {
        id: 'assistant-1',
        displayName: 'Hype AI',
        // avatarUrl?: string;
        role: 'assistant',
      },
  },
];

export const createInitialConversation = (participants: ChatUser[]): ChatConversation  =>  {
  return {
    id: 'conversation-1',
      title: 'Hype AI Welcome Conversation',
      participants,
    }
}

export type AdapterContext = {
  vectorStoreId: string | null;
  prevResponseId: string | null;
}

// Local echo adapter
export const createAdapter = (contextRef: { current: AdapterContext }): ChatAdapter => ({
  async sendMessage({ message, signal }) {
    return new ReadableStream({
      async start(controller) {
        const messageIds: string[] = []

        const { vectorStoreId, prevResponseId } = contextRef.current;

        if (!vectorStoreId) {
          throw new Error("Hype isn't ready yet. Please refresh or try again in a few minutes.")
        }

        try {
          const genericError = "Sorry, something went wrong. Please refresh or try again in a few minutes."

          let response: Response
          try {
            response = await apiFetch(`/api/hype/chat_hype`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({"prevResponseId": prevResponseId, "message": message.parts[0]?.type === 'text' ? message.parts[0].text : ''}),
              signal,
            })
          } catch {
            throw new Error(genericError)
          }

          let payload
          try {
            payload = await response.json();
          } catch {
            throw new Error(genericError)
          }

          if (!response.ok) {
            throw new Error(payload?.status_message ?? genericError)
          }

          const {data} = payload;
          if (!data?.output) {
            throw new Error(genericError)
          }

          let hypeResponse: HypeResponseModel
          try {
            hypeResponse = JSON.parse(data.output) as HypeResponseModel
          } catch {
            throw new Error(genericError)
          }
          const messageId = `msg-${new Date().getTime()}`;
          const textId = `text-${new Date().getTime()}`;
          messageIds.push(messageId)
          controller.enqueue({ type: 'start', messageId }); // Setup init message ID
          controller.enqueue({ type: 'text-start', id: textId });
          controller.enqueue({ type: 'text-delta', id: textId, delta: hypeResponse.answer ?? "" });
          controller.enqueue({ type: 'text-end', id: textId });

          (hypeResponse.choices ?? []).forEach((text: string, index: number) => {
            const messageId = `msg-choice-${new Date().getTime()}-${index + 1}`;
            const id = `text-${new Date().getTime()}-${index + 1}`;
            messageIds.push(messageId)
          
            controller.enqueue({ type: 'start', messageId });
            controller.enqueue({ type: 'text-start', id });
            controller.enqueue({ type: 'text-delta', id, delta: text });
            controller.enqueue({ type: 'text-end', id });

          });
          contextRef.current = { vectorStoreId, prevResponseId: data.responseId ?? null };
        } catch (error) {
          console.error({error})
          // A user/host-initiated abort isn't an error to report — let the stream close quietly.
          if (!signal?.aborted) {
            // Surface a real message to the user instead of leaving the chat stalled.
            const errorMessage = error instanceof Error ? error.message : "Sorry, something went wrong. Please refresh or try again in a few minutes."
            const errorMessageId = `msg-error-${new Date().getTime()}`;
            const errorTextId = `text-error-${new Date().getTime()}`;
            messageIds.push(errorMessageId)
            controller.enqueue({ type: 'start', messageId: errorMessageId });
            controller.enqueue({ type: 'text-start', id: errorTextId });
            controller.enqueue({ type: 'text-delta', id: errorTextId, delta: errorMessage });
            controller.enqueue({ type: 'text-end', id: errorTextId });
          }
        } finally {
          // On abort the consumer may have already torn down the stream, leaving the
          // controller closed; enqueue/close then throw, so ignore that here.
          try {
            messageIds.forEach((id) => {
              controller.enqueue({ type: 'finish', messageId: id });
            })
            controller.close();
          } catch {
            // stream already closed
          }
        }
      },
    });
  },
    async listConversations() {
    return {conversations: []};
  },
  async listMessages({ conversationId }) {
    return {
      messages: initialMessages || [],
      hasMore: false,
    };
  },
});

